import { useEffect, useMemo, useRef, useState } from "react";
import { Polyline, Polygon, CircleMarker, Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import { api, apiError } from "@/lib/api";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import { classifyBoundary } from "@/lib/geoClassify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Route as RouteIcon, Loader2, Trash2, Undo2, Eraser, MapPin, Pencil, Plus, Map as MapIcon, Wand2, ArrowUp, ArrowDown, X, Upload, Zap } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { key: "primary", label: "Primary trail", color: "#16A34A" },
  { key: "connector", label: "Connector", color: "#2563EB" },
  { key: "detour", label: "Detour", color: "#F97316" },
  { key: "attraction", label: "Attraction", color: "#8E24AA" },
];
const TYPE_COLOR = Object.fromEntries(TYPES.map((t) => [t.key, t.color]));
const UNCLASSIFIED = "Unclassified";
const stateOf = (p) => classifyBoundary(p.boundary || []).state || UNCLASSIFIED;
const pinIcon = (bg, label) => L.divIcon({ className: "", html: `<div style="background:${bg};color:#fff;width:22px;height:22px;border-radius:50%;border:2px solid #fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;box-shadow:0 1px 4px rgba(0,0,0,.4)">${label}</div>`, iconSize: [22, 22], iconAnchor: [11, 11] });

/**
 * Reusable route management surface.
 * - Global Index mode (default): shows State + Bike Path pickers, then the path's routes.
 * - Embedded mode: pass `embedBikePathId` (+ `embedPath` {name,boundary}, `embedState`) to
 *   lock to one bike path and hide the pickers — used inside the Bike Path editor Routes tab.
 * Phase 2 (snapping, GPX, multi-stop, turn-by-turn) must be built here so both surfaces share it.
 */
export default function RoutesManager({ embedBikePathId = null, embedPath = null, embedState = null }) {
  const embedded = !!embedBikePathId;
  const [paths, setPaths] = useState([]);
  const [counts, setCounts] = useState({});
  const [state, setState] = useState(embedState || "");
  const [bikePathId, setBikePathId] = useState(embedBikePathId || "");
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(!embedded);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stops, setStops] = useState([]);
  const [nonce, setNonce] = useState(0);
  const [mode, setMode] = useState("manual");
  const [genStart, setGenStart] = useState(null);
  const [genEnd, setGenEnd] = useState(null);
  const [genWps, setGenWps] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [resnapping, setResnapping] = useState(null);
  const fileRef = useRef(null);

  const withGeo = useMemo(() => paths.map((p) => ({ ...p, state: stateOf(p) })), [paths]);
  const states = useMemo(() => Array.from(new Set(withGeo.map((p) => p.state))).sort(), [withGeo]);
  const statePaths = useMemo(() => withGeo.filter((p) => p.state === state).sort((a, b) => a.name.localeCompare(b.name)), [withGeo, state]);
  const selectedPath = useMemo(() => (embedded ? embedPath : withGeo.find((p) => p.id === bikePathId)), [embedded, embedPath, withGeo, bikePathId]);

  const loadCounts = () => api.get("/admin/routes/counts").then(({ data }) => setCounts(data)).catch(() => {});

  useEffect(() => {
    if (embedded) return;
    (async () => {
      try {
        const [p] = await Promise.all([api.get("/bike-paths"), loadCounts()]);
        setPaths(p.data);
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => { if (embedBikePathId) setBikePathId(embedBikePathId); }, [embedBikePathId]);

  useEffect(() => {
    if (embedded || !states.length) return;
    if (!state || !states.includes(state)) setState(states[0]);
  }, [states]);
  useEffect(() => {
    if (embedded) return;
    if (!statePaths.length) { setBikePathId(""); return; }
    if (!statePaths.some((p) => p.id === bikePathId)) setBikePathId(statePaths[0].id);
  }, [state, statePaths.length]);

  const loadRoutes = async () => {
    if (!bikePathId) { setRoutes([]); return; }
    setLoadingRoutes(true);
    try { const { data } = await api.get("/admin/routes", { params: { linked_trail_id: bikePathId } }); setRoutes(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoadingRoutes(false); }
  };
  useEffect(() => { loadRoutes(); setEditing(null); }, [bikePathId]);

  useEffect(() => {
    if (!bikePathId) { setStops([]); return; }
    api.get("/rider/content", { params: { bike_path_id: bikePathId } })
      .then(({ data }) => setStops(data.filter((c) => c.kind !== "route" && c.lat != null)))
      .catch(() => setStops([]));
  }, [bikePathId]);

  const startNew = () => {
    if (!bikePathId) return toast.error("Select a bike path first");
    setEditing({ id: null, name: "", route_type: "primary", color: "#16A34A", coordinates: [], linked_stop_id: "", active: true });
    setMode("manual"); setGenStart(null); setGenEnd(null); setGenWps([]);
  };
  const startEdit = (r) => {
    const wps = r.waypoints || [];
    const coords = r.coordinates || [];
    setEditing({ id: r.id, name: r.name, route_type: r.route_type, color: r.color, coordinates: coords, linked_stop_id: r.linked_stop_id || "", active: r.active !== false, directions: r.directions || null });
    if ((r.source === "generated" || wps.length > 0) && coords.length >= 2) {
      setMode("auto"); setGenStart(coords[0]); setGenEnd(coords[coords.length - 1]); setGenWps(wps);
    } else { setMode("manual"); setGenStart(null); setGenEnd(null); setGenWps([]); }
  };

  const setType = (t) => setEditing((s) => ({ ...s, route_type: t, color: TYPE_COLOR[t] || s.color }));
  const addPoint = (lat, lng) => setEditing((s) => ({ ...s, coordinates: [...s.coordinates, [lat, lng]] }));
  const undoPoint = () => setEditing((s) => ({ ...s, coordinates: s.coordinates.slice(0, -1) }));
  const clearPoints = () => setEditing((s) => ({ ...s, coordinates: [] }));
  const clearPreview = () => setEditing((s) => ({ ...s, coordinates: [] }));
  const moveWp = (i, d) => { setGenWps((w) => { const a = [...w]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; }); clearPreview(); };
  const delWp = (i) => { setGenWps((w) => w.filter((_, k) => k !== i)); clearPreview(); };
  const setWp = (i, pt) => { setGenWps((w) => w.map((p, k) => (k === i ? pt : p))); clearPreview(); };

  const onMapClick = (lat, lng) => {
    if (mode === "manual") { addPoint(lat, lng); return; }
    if (!genStart) { setGenStart([lat, lng]); clearPreview(); }
    else if (!genEnd) { setGenEnd([lat, lng]); clearPreview(); }
    else { setGenWps((w) => [...w, [lat, lng]]); clearPreview(); }
  };
  const switchMode = (m) => { setMode(m); setGenStart(null); setGenEnd(null); setGenWps([]); setEditing((s) => ({ ...s, coordinates: [] })); };
  const generate = async () => {
    if (!genStart || !genEnd) return toast.error("Click a start and an end point on the map");
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/routes/generate", { start: genStart, end: genEnd, waypoints: genWps });
      setEditing((s) => ({ ...s, coordinates: data.coordinates, directions: data.directions || null }));
      setNonce((n) => n + 1);
      toast.success(`Route generated (${data.profile}${data.distance_m ? `, ${(data.distance_m / 1609.344).toFixed(1)} mi` : ""}${data.directions?.steps?.length ? ` · ${data.directions.steps.length} steps` : ""})`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Enter a route name");
    if (editing.coordinates.length < 2) return toast.error(mode === "auto" ? "Click Generate route first, then Save" : "Click at least 2 points on the map to draw the route");
    setSaving(true);
    const payload = {
      name: editing.name.trim(), route_type: editing.route_type, color: editing.color,
      coordinates: editing.coordinates, linked_trail_id: bikePathId,
      linked_stop_id: editing.linked_stop_id || null, active: editing.active,
      waypoints: mode === "auto" ? genWps : [], source: mode === "auto" ? "generated" : "manual",
      directions: editing.directions || null,
    };
    try {
      if (editing.id) await api.put(`/admin/routes/${editing.id}`, payload);
      else await api.post("/admin/routes", payload);
      toast.success(editing.id ? "Route updated" : "Route created");
      setEditing(null); await loadRoutes(); if (!embedded) loadCounts();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    try { await api.delete(`/admin/routes/${r.id}`); toast.success("Route deleted"); await loadRoutes(); if (!embedded) loadCounts(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onGpxFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!bikePathId) return toast.error("Select a bike path first");
    setImporting(true);
    try {
      const text = await file.text();
      const { data } = await api.post("/admin/routes/import-gpx", { gpx: text });
      setEditing({ id: null, name: data.name || file.name.replace(/\.gpx$/i, ""), route_type: "primary", color: "#16A34A", coordinates: data.coordinates, linked_stop_id: "", active: true });
      setMode("manual"); setGenStart(null); setGenEnd(null); setGenWps([]); setNonce((n) => n + 1);
      toast.success(`Imported ${data.points} points — review and Save`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const resnap = async (r) => {
    const c = r.coordinates || [];
    if (c.length < 2) return toast.error("Route has no geometry to re-snap");
    setResnapping(r.id);
    try {
      const n = c.length;
      const samples = Math.min(8, Math.max(0, n - 2));
      const wps = [];
      for (let k = 1; k <= samples; k++) wps.push(c[Math.round((k * (n - 1)) / (samples + 1))]);
      const { data } = await api.post("/admin/routes/generate", { start: c[0], end: c[n - 1], waypoints: wps });
      await api.put(`/admin/routes/${r.id}`, {
        name: r.name, route_type: r.route_type, color: r.color, coordinates: data.coordinates,
        linked_trail_id: r.linked_trail_id || bikePathId, linked_stop_id: r.linked_stop_id || null,
        active: r.active !== false, waypoints: wps, source: "generated", directions: data.directions || null,
      });
      toast.success(`Re-snapped to trail${data.distance_m ? ` · ${(data.distance_m / 1609.344).toFixed(1)} mi` : ""}${data.directions?.steps?.length ? ` · ${data.directions.steps.length} steps` : ""}`);
      await loadRoutes();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setResnapping(null); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  // ---------- Builder ----------
  if (editing) {
    const bnd = selectedPath?.boundary || [];
    const mapCenter = editing.coordinates[0] || bnd[0] || [41.75, -71.3];
    return (
      <div className="space-y-4" data-testid="route-builder">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-xl font-bold flex items-center gap-2"><RouteIcon className="w-5 h-5 text-primary" />{editing.id ? "Edit route" : "New route"}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{state || embedState} · {selectedPath?.name} — this route will belong to this bike path.</p>
          </div>
          <Button variant="ghost" onClick={() => setEditing(null)} data-testid="route-cancel-button">Cancel</Button>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <div>
            <div className="flex items-center gap-2 mb-3" data-testid="route-mode-toggle">
              <span className="text-sm text-muted-foreground">Route mode</span>
              <div className="inline-flex rounded-lg border border-border overflow-hidden">
                <button type="button" onClick={() => switchMode("manual")} data-testid="route-mode-manual" className={`px-3 py-1.5 text-sm transition-colors ${mode === "manual" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>Manual Draw</button>
                <button type="button" onClick={() => switchMode("auto")} data-testid="route-mode-auto" className={`px-3 py-1.5 text-sm transition-colors ${mode === "auto" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>Auto Generate</button>
              </div>
            </div>
            {mode === "manual" ? (
              <div className="mb-2 text-sm text-muted-foreground flex items-center gap-2"><MapPin className="w-4 h-4" />Click points on the map to draw the route (A → B → C → …). {editing.coordinates.length} point{editing.coordinates.length === 1 ? "" : "s"}.</div>
            ) : (
              <div className="mb-2 text-sm text-muted-foreground flex items-center gap-2"><Wand2 className="w-4 h-4" />Click a <b className="text-emerald-400">start</b>, then an <b className="text-rose-400">end</b> — then click to drop <b className="text-blue-400">waypoints</b> in between. Drag any marker to adjust, reorder/delete below, then Generate.</div>
            )}
            <LeafletMap height={520} center={mapCenter} onClick={onMapClick}>
              {bnd.length >= 3 && <Polygon positions={bnd} pathOptions={{ color: "#334155", weight: 2, fillOpacity: 0.04 }} />}
              <FitBounds bounds={editing.coordinates.length >= 2 ? editing.coordinates : bnd} nonce={nonce} />
              {editing.coordinates.length >= 2 && <Polyline positions={editing.coordinates} pathOptions={{ color: editing.color, weight: 5 }} />}
              {mode === "manual" && editing.coordinates.length <= 60 && editing.coordinates.map((pt, i) => (
                <CircleMarker key={i} center={pt} radius={6} pathOptions={{ color: "#fff", weight: 2, fillColor: editing.color, fillOpacity: 1 }}>
                  <Tooltip permanent direction="top">{i + 1}</Tooltip>
                </CircleMarker>
              ))}
              {mode === "auto" && genStart && (
                <Marker position={genStart} draggable eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setGenStart([ll.lat, ll.lng]); clearPreview(); } }} icon={pinIcon("#10B981", "S")} />
              )}
              {mode === "auto" && genEnd && (
                <Marker position={genEnd} draggable eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setGenEnd([ll.lat, ll.lng]); clearPreview(); } }} icon={pinIcon("#F43F5E", "E")} />
              )}
              {mode === "auto" && genWps.map((pt, i) => (
                <Marker key={`wp${i}`} position={pt} draggable eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setWp(i, [ll.lat, ll.lng]); } }} icon={pinIcon("#2563EB", String(i + 1))} />
              ))}
            </LeafletMap>
            {mode === "manual" ? (
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={undoPoint} disabled={!editing.coordinates.length} data-testid="route-undo-point"><Undo2 className="w-4 h-4 mr-1" />Undo point</Button>
                <Button size="sm" variant="outline" onClick={clearPoints} disabled={!editing.coordinates.length} data-testid="route-clear-points"><Eraser className="w-4 h-4 mr-1" />Clear</Button>
                <Button size="sm" variant="ghost" onClick={() => setNonce((n) => n + 1)} data-testid="route-recenter">Recenter</Button>
              </div>
            ) : (
              <div className="space-y-2 mt-2">
                <div className="flex flex-wrap gap-2 items-center">
                  <Button size="sm" onClick={generate} disabled={generating || !genStart || !genEnd} data-testid="route-generate-button">{generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}Generate route</Button>
                  <Button size="sm" variant="outline" onClick={() => { setGenStart(null); setGenEnd(null); setGenWps([]); setEditing((s) => ({ ...s, coordinates: [] })); }} disabled={!genStart && !genEnd && !genWps.length} data-testid="route-reset-auto"><Eraser className="w-4 h-4 mr-1" />Reset</Button>
                  {editing.coordinates.length >= 2 && <span className="text-xs text-emerald-400" data-testid="route-preview-note">Preview ready · {editing.coordinates.length} pts · review, then Save</span>}
                </div>
                {genWps.length > 0 && (
                  <div className="rounded-lg border border-border bg-card p-2 space-y-1" data-testid="waypoint-list">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground px-1">Waypoints ({genWps.length}) · reorder / delete, then re-generate</div>
                    {genWps.map((pt, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs" data-testid={`waypoint-row-${i}`}>
                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shrink-0">{i + 1}</span>
                        <span className="flex-1 text-muted-foreground truncate">{pt[0].toFixed(4)}, {pt[1].toFixed(4)}</span>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => moveWp(i, -1)} disabled={i === 0} data-testid={`waypoint-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => moveWp(i, 1)} disabled={i === genWps.length - 1} data-testid={`waypoint-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => delWp(i)} data-testid={`waypoint-del-${i}`}><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4 rounded-xl border border-border bg-card p-4">
            <div className="rounded-lg border border-border bg-secondary/50 p-3" data-testid="route-trail-context">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Belongs to</div>
              <div className="font-heading font-bold text-sm flex items-center gap-1.5"><MapIcon className="w-4 h-4 text-primary" />{selectedPath?.name}</div>
              <div className="text-xs text-muted-foreground">{state || embedState}</div>
            </div>
            <div>
              <Label>Route name</Label>
              <Input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} placeholder="East Bay Bike Path" className="mt-1.5" data-testid="route-name-input" />
            </div>
            <div>
              <Label>Route type</Label>
              <Select value={editing.route_type} onValueChange={setType}>
                <SelectTrigger className="mt-1.5" data-testid="route-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input type="color" value={editing.color} onChange={(e) => setEditing((s) => ({ ...s, color: e.target.value }))} className="h-9 w-12 rounded border border-border bg-secondary" data-testid="route-color-input" />
                <Input value={editing.color} onChange={(e) => setEditing((s) => ({ ...s, color: e.target.value }))} className="w-28" />
              </div>
            </div>
            <div>
              <Label>Link to stop (optional)</Label>
              <Select value={editing.linked_stop_id || "none"} onValueChange={(v) => setEditing((s) => ({ ...s, linked_stop_id: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1.5" data-testid="route-stop-select"><SelectValue placeholder="Select a stop" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{stops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Riders see a "Show Route" button on this stop to reveal the route.</p>
            </div>
            <div className="flex items-center gap-2"><Switch checked={editing.active} onCheckedChange={(v) => setEditing((s) => ({ ...s, active: v }))} data-testid="route-active-switch" /><span className="text-sm">Active (visible to riders)</span></div>
            {editing.directions?.steps?.length > 0 && (
              <div className="rounded-xl border border-border bg-background/40 p-3" data-testid="route-directions">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-heading font-bold text-sm">Turn-by-turn ({editing.directions.steps.length})</span>
                  <span className="text-xs text-muted-foreground">{editing.directions.total_distance_text} · {editing.directions.total_duration_text}</span>
                </div>
                <ol className="space-y-1 max-h-48 overflow-y-auto text-sm">
                  {editing.directions.steps.map((st, i) => (
                    <li key={i} className="flex gap-2" data-testid={`route-direction-step-${i}`}>
                      <span className="text-muted-foreground w-5 shrink-0">{i + 1}.</span>
                      <span className="flex-1">{st.instruction}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{st.distance_text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <Button className="w-full" onClick={save} disabled={saving} data-testid="route-save-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing.id ? "Save changes" : "Create route"}</Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- List ----------
  const listHeader = (
    <div className="flex items-center gap-2 text-sm">
      <span className="font-heading font-bold text-base">{selectedPath?.name}</span>
      <span className="text-muted-foreground" data-testid="routes-count">({routes.length} {routes.length === 1 ? "Route" : "Routes"})</span>
      {(state || embedState) && <span className="text-xs text-muted-foreground">· {state || embedState}</span>}
    </div>
  );

  const list = (
    loadingRoutes ? (
      <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
    ) : routes.length === 0 ? (
      <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="routes-empty">No routes yet for this bike path. Create the first one.</div>
    ) : (
      <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="routes-list">
        {routes.map((r) => (
          <div key={r.id} className="flex items-center gap-3 p-3" data-testid={`route-row-${r.id}`}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: r.color }} />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{r.name}{r.active === false ? <span className="text-xs text-muted-foreground"> (inactive)</span> : ""}</div>
              <div className="text-xs text-muted-foreground truncate">
                <span className="uppercase tracking-wide font-bold" style={{ color: r.color }}>{r.route_type}</span>
                {r.linked_stop_id ? " · linked to stop" : ""}
                {" · "}{r.coordinates?.length || 0} pts
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid={`route-edit-${r.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
            {(r.coordinates?.length || 0) >= 2 && (
              <Button size="sm" variant="outline" onClick={() => resnap(r)} disabled={resnapping === r.id} title="Snap this route's geometry to the real trail via the routing engine" data-testid={`route-resnap-${r.id}`}>
                {resnapping === r.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}Re-snap
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive" data-testid={`route-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete "{r.name}"?</AlertDialogTitle><AlertDialogDescription>This permanently removes the route from the Rider map. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(r)} data-testid={`route-confirm-delete-${r.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))}
      </div>
    )
  );

  // Embedded: no pickers, just the path's routes + a New route button.
  if (embedded) {
    return (
      <div className="space-y-4" data-testid="routes-panel">
        <input ref={fileRef} type="file" accept=".gpx,application/gpx+xml" className="hidden" onChange={onGpxFile} data-testid="route-gpx-input" />
        <div className="flex items-center justify-between">
          {listHeader}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="route-import-gpx-button">{importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Import GPX</Button>
            <Button onClick={startNew} data-testid="route-new-button"><Plus className="w-4 h-4 mr-1" />New route</Button>
          </div>
        </div>
        {list}
      </div>
    );
  }

  // Global Route Index: State -> Bike Path -> Routes
  return (
    <div className="space-y-4" data-testid="routes-panel">
      <div>
        <h3 className="font-heading text-xl font-bold flex items-center gap-2"><RouteIcon className="w-5 h-5 text-primary" />Global Route Index</h3>
        <p className="text-sm text-muted-foreground mt-0.5">Cross-path route search and QA. Pick a state and bike path to manage its routes — or manage them inside Bike Paths → Routes tab. Every route belongs to a bike path.</p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="w-56">
          <Label>State</Label>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger className="mt-1.5" data-testid="routes-state-select"><SelectValue placeholder="Select a state" /></SelectTrigger>
            <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-72">
          <Label>Bike path</Label>
          <Select value={bikePathId} onValueChange={setBikePathId} disabled={!statePaths.length}>
            <SelectTrigger className="mt-1.5" data-testid="routes-path-select"><SelectValue placeholder={statePaths.length ? "Select a bike path" : "No bike paths in this state"} /></SelectTrigger>
            <SelectContent>{statePaths.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({counts[p.id] || 0})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={startNew} disabled={!bikePathId} data-testid="route-new-button"><Plus className="w-4 h-4 mr-1" />New route</Button>
        <input ref={fileRef} type="file" accept=".gpx,application/gpx+xml" className="hidden" onChange={onGpxFile} data-testid="route-gpx-input" />
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!bikePathId || importing} data-testid="route-import-gpx-button">{importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Import GPX</Button>
      </div>

      {!bikePathId ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="routes-no-path">Select a state and bike path to manage its routes.</div>
      ) : (
        <>
          {listHeader}
          {list}
        </>
      )}
    </div>
  );
}
